import * as Sentry from '@sentry/browser';
import { createBrowserHistory as createHistory } from 'history';
import React from 'react';
import { createRoot } from 'react-dom/client';
// tslint:disable: no-import-side-effect
import 'ts-polyfill/lib/es2015-collection';
import 'ts-polyfill/lib/es2015-core';
import 'ts-polyfill/lib/es2017-object';
import 'ts-polyfill/lib/es2019-array';
import 'ts-polyfill/lib/es2019-object';
import 'ts-polyfill/lib/es2019-symbol';
import 'ts-polyfill/lib/es2020-promise';
// tslint:enable: no-import-side-effect
import WebFontLoader from 'webfontloader';
import { fetchAccessTokenIfNeeded, login } from '~/AccessToken';
import Application from '~/Application';
import { checkApplicationUpdateEvery } from '~/ApplicationUpdate';
import config from '~/config';
import { handleError, installErrorLogger } from '~/ErrorHandler';
import { logGoogleTagManagerCurrentUser } from '~/GoogleTagManager';
import { setupItemsPerPage } from '~/GroupProduct';
import { init as initLogger, logError, setupUserContext } from '~/Logger';
import Router from '~/Router';
import { createStore, Services, Store } from '~/Store';
import AuthenticatedApiClient from './ApiClient/AuthenticatedApiClient';
import UserProductGroupApi from './Api/User/ProductGroup/UserProductGroupApi';

import createBrowserApiClient from './createBrowserApiClient';
import ErrorHandlerManager from './ErrorHandler/ErrorHandlerManager';
import GroupProductManager from './GroupProduct/GroupProductManager';
import { ServiceContext } from './ServiceContext';
import CopyGroupProductManager from './CopyGroupProduct/CopyGroupProductManager';
import PersistentStateManager from './PersistentState/PersistentStateManager';
import HooksApiContainer from './HooksApiContainer';
import { BASE_SCENARIO_ID } from '~/ERP/ERPScenarioSelect';
import { ApiConnector } from './infrastructure/connectors/ApiConnector';

declare const SENTRY_DSN: string | null;
declare const SENTRY_TUNNEL: string | null;
declare const ENV_NAME: string;
declare const BUILD: string;
declare const API_BASE_URL: string | null;
declare const ENABLE_REDUX_LOGGER: boolean;
declare const GOOGLE_TAG_MANAGER_CONTAINER_ID: string;

// @ts-expect-error just putting COMMIT SHA to the window so that we know what's deployed
window.BUILD = BUILD;

window.addEventListener('unhandledrejection', handleError);

const history = createHistory();
const apiBaseUrl = API_BASE_URL !== null ? API_BASE_URL : config.api.baseUrl;
const apiClient = createBrowserApiClient(apiBaseUrl);

// @ts-expect-error this will throw an unassigned error
let servicesInstance: Services = {};

export type DispatchGetState = { dispatch: Store['dispatch']; getState: Store['getState'] };

const store: Store = createStore(
	{
		apiClient: {
			client: apiClient,
		},
		applicationUpdate: {
			currentBuild: BUILD,
			availableVersion: null,
		},
		erpScenarioComparisonState: {
			leftSelectedScenarioId: BASE_SCENARIO_ID,
			rightSelectedScenarioId: null,
		},
	},
	history,
	(dispatch: Store['dispatch'], getState: Store['getState']) => {
		const authenticatedApiClient = new AuthenticatedApiClient(getState, apiClient);
		const errorHandlerManager = new ErrorHandlerManager(dispatch, authenticatedApiClient);
		errorHandlerManager.start();

		const userProductGroupApi = new UserProductGroupApi(authenticatedApiClient);
		const groupProductManager = new GroupProductManager({ dispatch, getState }, { userProductGroupApi });

		servicesInstance = {
			apiClient,
			authenticatedApiClient,
			copyGroupProductManager: new CopyGroupProductManager({ groupProductManager }),
			errorHandlerManager,
			history,
			groupProductManager,
			persistentStateManager: new PersistentStateManager({ dispatch, getState }, { window }),
			userProductGroupApi,
			window,
		};

		return servicesInstance;
	},
	ENABLE_REDUX_LOGGER ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ : null
);

if (SENTRY_DSN !== null) {
	initLogger({
		sentryDsn: SENTRY_DSN,
		sentryTunnel: SENTRY_TUNNEL,
		environment: ENV_NAME,
		release: BUILD,
		// TODO: resolve after bumping react-router-dom
		resolveUrlPath: () => (store.getState().router.location as any).pathname,
	});
	installErrorLogger(logError);
}

window.addEventListener('beforeunload', () => {
	servicesInstance.errorHandlerManager.destroy();
	servicesInstance.persistentStateManager.persistCurrentUser();
});

const STATE_SAVE_INTERVAL = 10 * 60 * 1000; // Every 10 minutes

window.setInterval(() => {
	servicesInstance.persistentStateManager.persistCurrentUser();
}, STATE_SAVE_INTERVAL);

window.addEventListener('focus', async () => {
	try {
		await store.dispatch(fetchAccessTokenIfNeeded());
	} catch (error) {
		// ignore
	}
});

WebFontLoader.load(config.webFontLoader);

if (window.Cypress) {
	window.testReduxStore = store;
	window.testLoginAction = login;
	window.testSetupGroupProductsPerPage = setupItemsPerPage;
}

const root = createRoot(document.getElementById('application') as HTMLElement);

root.render(
	<ServiceContext.Provider value={servicesInstance}>
		<HooksApiContainer apiBaseUrl={apiBaseUrl} store={store}>
			<ApiConnector>
				<Application
					googleTagManagerContainerId={GOOGLE_TAG_MANAGER_CONTAINER_ID}
					store={store}
					onError={logError}
					onLoad={() => {
						store.dispatch(setupUserContext(fetchAccessTokenIfNeeded));
						store.dispatch(logGoogleTagManagerCurrentUser());
						store.dispatch(checkApplicationUpdateEvery(config.applicationUpdate.hoursInterval));
					}}
				>
					<Router history={history} />
				</Application>
			</ApiConnector>
		</HooksApiContainer>
	</ServiceContext.Provider>
);
